import { useEffect } from 'react'
import { CardGroup, Col, Container, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { DashboardNavigationCard } from 'Modules/dashboard/components/DashboardNavigationCard/DashboardNavigationCard'
import { DashboardNavigationCardIconEnum } from 'Modules/dashboard/components/DashboardNavigationCard/DashboardNavigationCard.types'

import { Card, CardBody, Heading, PrimaryButton } from 'Components/_theme'
import { ImpactTracker } from 'Components/ImpactTracker/ImpactTracker'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { NewPhonePlanModal } from 'Modals/NewPhonePlanModal/NewPhonePlanModal'

import { useStore } from 'Stores/index'

import { useModal } from 'Hooks'

import { getFullContactName } from 'Utilities'

import {
  E2E_DEPOTS_CARD,
  E2E_EMPLOYEES_CARD,
  E2E_INVOICES_CARD,
  E2E_NEW_PHONEPLAN_BUTTON,
  E2E_ORDERS_CARD,
  E2E_PHONEPLANS_CARD,
  E2E_RENTALS_CARD,
} from 'Constants/e2e'
import { PortalSectionEnum } from 'Constants/portalSection'

import { PageTypeEnum } from 'Types/page/page.types'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

export const OrganisationDashboardLegacy = observer(() => {
  const { portalSettingsStore, portalConfigStore, organisationStore } =
    useStore()
  const { setCurrentPortalSection, isAppReadOnly } = portalSettingsStore
  const { portalConfig } = portalConfigStore
  const { fetchOrganisation, isLoadingOrganisation, organisation } =
    organisationStore

  const { setModal, hideModal } = useModal()

  const history = useHistory()

  useEffect(() => {
    setCurrentPortalSection(PortalSectionEnum.Dashboard)
  }, [setCurrentPortalSection])

  const openNewPhonePlanModal = () => {
    setModal({
      component: NewPhonePlanModal,
      isOpen: true,
      props: {
        handleSubmit: () => {
          fetchOrganisation(organisation?.id || '')
          hideModal()
        },
        hide: hideModal,
        page: PageTypeEnum.Overview,
      },
    })
  }

  if (isLoadingOrganisation) {
    return <PageLoadingState />
  }

  if (!organisation) {
    return <PageErrorState />
  }

  const {
    phonePlans,
    employees,
    invoices,
    caseItems,
    rentals,
    depots,
    id: organisationId,
  } = organisation

  const showImpactTracker =
    portalConfig?.impactTracker && organisation?.impactFactors

  return (
    <Container className="pt-6" fluid>
      <div>
        <PageHeader portalSection={PortalSectionEnum.Dashboard} />
      </div>

      <Row>
        <Col xs={12} xxl={showImpactTracker ? 8 : 12}>
          <CardGroup>
            {rentals && rentals.totalCount !== 0 && (
              <DashboardNavigationCard
                e2eSelector={E2E_RENTALS_CARD}
                icon={DashboardNavigationCardIconEnum.Rentals}
                rentals={rentals.rentals}
                title="Rentals"
                total={rentals.totalCount}
                url={composePath({
                  params: { organisationId },
                  path: GLOBAL_ROUTE_PATHS.organisation.rentals,
                })}
              />
            )}

            {employees && employees.totalCount !== 0 && (
              <DashboardNavigationCard
                e2eSelector={E2E_EMPLOYEES_CARD}
                icon={DashboardNavigationCardIconEnum.Employees}
                rentals={null}
                title="Employees"
                total={employees.totalCount}
                url={composePath({
                  params: { organisationId },
                  path: GLOBAL_ROUTE_PATHS.organisation.employees,
                })}
              />
            )}

            {depots && depots.totalCount !== 0 && (
              <DashboardNavigationCard
                e2eSelector={E2E_DEPOTS_CARD}
                icon={DashboardNavigationCardIconEnum.Depots}
                rentals={null}
                stocklistItemCount={depots.stocklistItemCount}
                title="Depots"
                total={depots.totalCount}
                url={composePath({
                  params: { organisationId },
                  path: GLOBAL_ROUTE_PATHS.organisation.depots,
                })}
              />
            )}

            {caseItems && caseItems.totalCount !== 0 && (
              <DashboardNavigationCard
                caseItems={caseItems}
                e2eSelector={E2E_ORDERS_CARD}
                icon={DashboardNavigationCardIconEnum.CaseItems}
                rentals={null}
                title="Orders"
                total={
                  caseItems.countByStatus
                    ? caseItems.countByStatus
                        .map(countByStatus => countByStatus.count)
                        .reduce((prev, curr) => prev + curr)
                    : 0
                }
                url={composePath({
                  params: { organisationId },
                  path: GLOBAL_ROUTE_PATHS.organisation.orders,
                })}
              />
            )}

            {phonePlans && phonePlans.totalCount !== 0 && (
              <DashboardNavigationCard
                e2eSelector={E2E_PHONEPLANS_CARD}
                icon={DashboardNavigationCardIconEnum.PhonePlans}
                rentals={null}
                title="Phone_plans"
                total={phonePlans.totalCount}
                url={composePath({
                  params: { organisationId },
                  path: GLOBAL_ROUTE_PATHS.organisation.phonePlans,
                })}
              />
            )}

            {invoices && invoices.totalCount !== 0 && (
              <DashboardNavigationCard
                e2eSelector={E2E_INVOICES_CARD}
                icon={DashboardNavigationCardIconEnum.Invoices}
                rentals={null}
                title="Invoices"
                total={invoices.totalCount}
                url={composePath({
                  params: { organisationId },
                  path: GLOBAL_ROUTE_PATHS.organisation.invoices,
                })}
              />
            )}
          </CardGroup>

          {!isAppReadOnly &&
            portalConfig?.newDevice === true &&
            portalConfig?.showNewDeviceButtonToAdmin === true && (
              <PrimaryButton
                className="mb-4 w-100"
                onClick={() => {
                  history.push(
                    composePath({
                      params: { organisationId },
                      path: GLOBAL_ROUTE_PATHS.organisation.orderNew,
                    }),
                  )
                }}
              >
                <FormattedMessage id="New_device" />
              </PrimaryButton>
            )}

          {!isAppReadOnly && portalConfig?.newPhonePlan && (
            <PrimaryButton
              className="mb-6 w-100"
              data-e2e={E2E_NEW_PHONEPLAN_BUTTON}
              onClick={openNewPhonePlanModal}
            >
              <FormattedMessage id="New_phone_plan" />
            </PrimaryButton>
          )}

          {organisation?.accountManager && (
            <Card
              key={getFullContactName(organisation.accountManager)}
              className="mb-4"
            >
              <CardBody>
                <Heading as={5} size={2}>
                  <FormattedMessage id="Dashboard_everphone_contact" />
                </Heading>

                <div className="mt-2">
                  {getFullContactName(organisation.accountManager)}
                </div>
              </CardBody>
            </Card>
          )}
        </Col>

        {showImpactTracker && (
          <Col xs={12} xxl={4}>
            <ImpactTracker />
          </Col>
        )}
      </Row>
    </Container>
  )
})
